import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Copy, Trash2, ExternalLink, Edit2, RefreshCw, Archive, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { madridLocalToIsoUtc, previewMadrid } from '../../lib/madridTime';

const STATUS_STYLES = {
  draft:     { label: 'Borrador',  cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  published: { label: 'Publicada', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  archived:  { label: 'Archivada', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const PHASE_LABELS = {
  upcoming:  'Próxima',
  live:      'En vivo',
  recording: 'Grabación 48h',
  past:      'Finalizada',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Madrid' });
  } catch { return iso; }
};

export default function AdminMasterclass() {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ title: '', date_local: '', duration_minutes: 90 });
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get('/admin/masterclasses');
      setItems(r.data || []);
    } catch (e) {
      toast.error('Error al cargar masterclasses');
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newForm.title.trim()) { toast.error('Pon un título'); return; }
    if (!newForm.date_local.trim()) { toast.error('Elige la fecha y hora (Madrid) del encuentro'); return; }
    setCreating(true);
    try {
      const payload = {
        title: newForm.title,
        duration_minutes: newForm.duration_minutes,
        date_iso: madridLocalToIsoUtc(newForm.date_local),
      };
      const r = await api.post('/admin/masterclasses', payload);
      toast.success('Masterclass creada');
      setShowNew(false);
      setNewForm({ title: '', date_local: '', duration_minutes: 90 });
      navigate(`/admin/masterclass/${r.data.slug}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear');
    } finally { setCreating(false); }
  };

  const clone = async (slug) => {
    try {
      const r = await api.post(`/admin/masterclasses/${slug}/clone`);
      toast.success('Clonada como borrador');
      navigate(`/admin/masterclass/${r.data.slug}`);
    } catch { toast.error('Error al clonar'); }
  };

  const archive = async (slug) => {
    if (!window.confirm('¿Archivar esta masterclass? Dejará de aparecer en la web.')) return;
    try {
      await api.put(`/admin/masterclasses/${slug}`, { status: 'archived', show_homepage_banner: false });
      toast.success('Archivada');
      load();
    } catch { toast.error('Error al archivar'); }
  };

  const publish = async (slug) => {
    try {
      await api.put(`/admin/masterclasses/${slug}`, { status: 'published' });
      toast.success('Publicada');
      load();
    } catch { toast.error('Error al publicar'); }
  };

  const remove = async (slug, title) => {
    if (!window.confirm(`Eliminar "${title}" y todas sus inscripciones. Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/masterclasses/${slug}`);
      toast.success('Eliminada');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  if (!items) {
    return <div className="pt-20 min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="pt-20 min-h-screen bg-[#FDFCFA] pb-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between gap-4 mb-8 flex-col sm:flex-row">
          <div>
            <p className="text-xs text-[hsl(var(--text-tertiary))] uppercase tracking-wider mb-1">Admin · Cursos</p>
            <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))] flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#c9a227]" /> Masterclasses
            </h1>
            <p className="text-sm text-[hsl(var(--text-secondary))] mt-1">
              Crea, clona y archiva masterclasses gratuitas. Solo una se muestra al público a la vez (la más relevante).
            </p>
          </div>
          <Button onClick={() => setShowNew(s => !s)} className="bg-[#c9a227] hover:bg-[#b89220] text-white" data-testid="admin-mc-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva masterclass
          </Button>
        </div>

        {/* New masterclass form (inline) */}
        {showNew && (
          <div className="card-soft mb-8 space-y-4" data-testid="admin-mc-new-form">
            <h2 className="text-lg font-heading">Nueva masterclass</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-[hsl(var(--text-secondary))]">Título</label>
                <input value={newForm.title} onChange={e => setNewForm({ ...newForm, title: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm" placeholder="Ej.: El segundo límite: la voz" data-testid="admin-mc-new-title" />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-[hsl(var(--text-secondary))]">Fecha y hora (hora de Madrid)</label>
                <input
                  type="datetime-local"
                  value={newForm.date_local}
                  onChange={e => setNewForm({ ...newForm, date_local: e.target.value })}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                  data-testid="admin-mc-new-date"
                />
                {newForm.date_local && (
                  <p className="text-[11px] text-[#8a7428]">
                    {previewMadrid(madridLocalToIsoUtc(newForm.date_local))}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={creating} className="bg-[#c9a227] hover:bg-[#b89220] text-white" data-testid="admin-mc-new-save">
                {creating ? 'Creando…' : 'Crear y editar'}
              </Button>
            </div>
            <p className="text-[11px] text-[hsl(var(--text-tertiary))]">
              Se crea como borrador. Después podrás añadir descripción, imagen, link de Meet, y publicarla.
            </p>
          </div>
        )}

        {/* List */}
        {items.length === 0 ? (
          <div className="card-soft text-center py-14">
            <Sparkles className="w-10 h-10 mx-auto text-[#c9a227]/60 mb-3" />
            <p className="text-sm text-[hsl(var(--text-secondary))]">Todavía no hay masterclasses. Crea la primera.</p>
          </div>
        ) : (
          <div className="card-soft overflow-hidden p-0" data-testid="admin-mc-list">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#faf7f2] border-b border-[hsl(var(--border))]">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[hsl(var(--text-tertiary))]">
                    <th className="px-5 py-3">Título</th>
                    <th className="px-5 py-3">Fecha</th>
                    <th className="px-5 py-3">Estado</th>
                    <th className="px-5 py-3">Fase</th>
                    <th className="px-5 py-3 text-center">Inscritas</th>
                    <th className="px-5 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(var(--border))]">
                  {items.map(m => {
                    const st = STATUS_STYLES[m.status] || STATUS_STYLES.draft;
                    return (
                      <tr key={m.slug} className="hover:bg-[#fdfbf5]" data-testid={`admin-mc-row-${m.slug}`}>
                        <td className="px-5 py-4">
                          <Link to={`/admin/masterclass/${m.slug}`} className="font-medium text-[hsl(var(--text-primary))] hover:text-[#c9a227]">
                            {m.title}
                          </Link>
                          {m.show_homepage_banner && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-[#c9a227]/10 text-[#8a7428]">Banner Home</span>
                          )}
                          <p className="text-[11px] text-[hsl(var(--text-tertiary))] font-mono mt-0.5">{m.slug}</p>
                        </td>
                        <td className="px-5 py-4 text-[hsl(var(--text-secondary))]">{fmt(m.date_iso)}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] border ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-5 py-4 text-[hsl(var(--text-secondary))]">{PHASE_LABELS[m.phase] || '—'}</td>
                        <td className="px-5 py-4 text-center font-medium">{m.enrollments_count || 0}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <Link to={`/masterclass/${m.slug}`} target="_blank" title="Ver landing" className="p-2 rounded-md hover:bg-[#faf6ed] text-[hsl(var(--text-secondary))]">
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                            <Link to={`/admin/masterclass/${m.slug}`} title="Editar" className="p-2 rounded-md hover:bg-[#faf6ed] text-[hsl(var(--text-secondary))]" data-testid={`admin-mc-edit-${m.slug}`}>
                              <Edit2 className="w-4 h-4" />
                            </Link>
                            <button onClick={() => clone(m.slug)} title="Clonar" className="p-2 rounded-md hover:bg-[#faf6ed] text-[hsl(var(--text-secondary))]" data-testid={`admin-mc-clone-${m.slug}`}>
                              <Copy className="w-4 h-4" />
                            </button>
                            {m.status === 'published' ? (
                              <button onClick={() => archive(m.slug)} title="Archivar" className="p-2 rounded-md hover:bg-amber-50 text-amber-700" data-testid={`admin-mc-archive-${m.slug}`}>
                                <Archive className="w-4 h-4" />
                              </button>
                            ) : m.status === 'draft' ? (
                              <button onClick={() => publish(m.slug)} title="Publicar" className="p-2 rounded-md hover:bg-emerald-50 text-emerald-700" data-testid={`admin-mc-publish-${m.slug}`}>
                                <RefreshCw className="w-4 h-4" />
                              </button>
                            ) : (
                              <button onClick={() => publish(m.slug)} title="Despublicar de archivo" className="p-2 rounded-md hover:bg-emerald-50 text-emerald-700" data-testid={`admin-mc-publish-${m.slug}`}>
                                <RefreshCw className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => remove(m.slug, m.title)} title="Eliminar" className="p-2 rounded-md hover:bg-red-50 text-red-600" data-testid={`admin-mc-delete-${m.slug}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-[11px] text-[hsl(var(--text-tertiary))] mt-4">
          La masterclass mostrada al público (banner, nav, /mi-camino) es la <strong>publicada</strong> más relevante: prioridad <em>en vivo &gt; grabación &gt; próxima</em>.
        </p>
      </div>
    </div>
  );
}
